import { useCallback, useEffect, useMemo } from 'react';
import { useFormikContext } from 'formik';
import type { ChangeEvent } from 'react';

import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Grid from '@mui/material/Grid';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';

import { isDefined } from '@e-socna/utils';
import { SubMissionActorCaseEnum } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useWizardContext } from '../../../components/Wizard/contexts/WizardContext/hooks/useWizardContext.hook';
import { useParentActorsNotice } from '../../../components/Actors/hooks/useParentActorsNotice.hook';
import { ErrorsAlert } from '../../../components/ErrorsAlert';
import { NoticesAlert } from '../../../components/NoticesAlert';
import { useParentActorsLevel } from '../../../components/Actors/hooks/useParentActorsLevel.hook';

import { QuotationActorSelect } from '../components/QuotationActorSelect';
import { useQuotationCollaboration } from '../hooks/useQuotationCollaboration.hook';
import { useSubMissionQuotationCase } from '../hooks/useSubMissionQuotationCase.hook';
import { useMissionCreationRefusal } from '../hooks/useMissionCreationRefusal.hook';
import {
  buildInternalQuotationActors,
  externalQuotationInternalActorsOf,
  QUOTATION_CASE_LABELS,
  QUOTATION_CASES,
} from '../utils/quotation-case.util';
import type { MissionForm_Mission } from '../fragments/mission.fragment';

interface Props {
  readonly id: string;
  readonly isCreation: boolean;
}

// The actors of a sub-mission created from its collaboration follow its
// quotation case: the collaboration ones for an external quotation; for an
// internal quotation, the chosen internal actor of the collaboration quotes
// the collaboration emitter.
export const MissionSubMissionActorsFormStep = ({
  isCreation,
}: Readonly<Props>) => {
  const { errors, setValues, values } = useFormikContext<MissionForm_Mission>();
  const { setIsSubmitLocked } = useWizardContext();

  // The creation takes the roles held through the internal actors: the step tells a refusal.
  const creationRefusal = useMissionCreationRefusal(isCreation);
  useEffect(() => {
    setIsSubmitLocked(!!creationRefusal);

    return () => {
      setIsSubmitLocked(false);
    };
  }, [creationRefusal, setIsSubmitLocked]);
  const { actorCategoriesCodeMap } = useReferencesContext();
  const { quotationCase, workflowIdOfCase } = useSubMissionQuotationCase(
    values.workflowId,
  );
  const {
    actorOptions,
    collaborationEmitter,
    emitterThirdPartyId,
    formActorOf,
    hasSeveralInternalActors,
    isLoaded,
    quotationEmitterCandidates,
    receiverThirdPartyId,
  } = useQuotationCollaboration(values.compositeMissionId);

  // An internal quotation is emitted by an internal actor of the collaboration.
  const parent = useParentActorsLevel({
    folderId: values.folderId,
    collaborationId: values.compositeMissionId,
  });

  const isInternalQuotation =
    quotationCase === SubMissionActorCaseEnum.InternalRecharging;
  const parentNotice = useParentActorsNotice({ isInternal: true, parent });
  // Without another internal actor in the collaboration, no quotation emitter can be chosen: the
  // action editing its internal actors goes with the refusal.
  const lacksQuotationEmitterCandidate =
    isLoaded && isInternalQuotation && !quotationEmitterCandidates.length;
  const refusals = [
    creationRefusal ? { message: creationRefusal } : null,
    lacksQuotationEmitterCandidate
      ? {
          message:
            "La Collaboration n'a pas d'autre acteur interne que son émetteur du devis : ajoutez-en un à la Collaboration pour un devis interne.",
          action: parentNotice.action,
        }
      : null,
  ].filter(isDefined);
  const notices = [
    lacksQuotationEmitterCandidate
      ? { message: parentNotice.message }
      : parentNotice,
  ];
  const emitterCategoryId = actorCategoriesCodeMap.QuotationEmitter._id;
  const chosenEmitterThirdPartyId = values.internalActors?.find((actor) =>
    actor.categoryIds.includes(emitterCategoryId),
  )?.thirdPartyId;

  const externalQuotationInternalActors = useMemo(
    () =>
      externalQuotationInternalActorsOf(
        collaborationEmitter,
        hasSeveralInternalActors,
      ),
    [collaborationEmitter, hasSeveralInternalActors],
  );

  useEffect(() => {
    if (
      isLoaded &&
      !isInternalQuotation &&
      !values.internalActors &&
      externalQuotationInternalActors
    ) {
      void setValues({
        ...values,
        internalActors: externalQuotationInternalActors,
      });
    }
  }, [
    externalQuotationInternalActors,
    isInternalQuotation,
    isLoaded,
    setValues,
    values,
  ]);

  // One update: validating the workflow and the actors one after the other
  // would check the new actors against the former workflow.
  const handleQuotationCaseChange = useCallback(
    (event: Readonly<ChangeEvent<HTMLInputElement>>) => {
      const quotationCase = event.target.value as SubMissionActorCaseEnum;

      void setValues({
        ...values,
        workflowId: workflowIdOfCase(quotationCase) ?? values.workflowId,
        internalActors:
          quotationCase === SubMissionActorCaseEnum.SameEmitterAndReceiver
            ? externalQuotationInternalActors
            : null,
        externalActors: null,
      });
    },
    [externalQuotationInternalActors, setValues, values, workflowIdOfCase],
  );

  const handleEmitterChange = useCallback(
    (event: Readonly<ChangeEvent<HTMLInputElement>>) => {
      const emitter = formActorOf(event.target.value as Id);

      if (!emitter || !collaborationEmitter) {
        return;
      }

      void setValues({
        ...values,
        ...buildInternalQuotationActors({
          emitter,
          collaborationEmitter,
          categoryIds: {
            emitterCategoryId,
            receiverCategoryId: actorCategoriesCodeMap.QuotationReceiver._id,
          },
        }),
      });
    },
    [
      actorCategoriesCodeMap.QuotationReceiver._id,
      collaborationEmitter,
      emitterCategoryId,
      formActorOf,
      setValues,
      values,
    ],
  );

  return (
    <Grid container spacing={2} sx={{ alignContent: 'flex-start', pt: 2 }}>
      <Grid size={12}>
        <FormControl>
          <FormLabel id="sub-mission-quotation-case-label">
            Configuration
          </FormLabel>
          <RadioGroup
            aria-labelledby="sub-mission-quotation-case-label"
            name="sub-mission-quotation-case"
            onChange={handleQuotationCaseChange}
            row
            value={quotationCase}
          >
            {QUOTATION_CASES.map((subMissionActorCase) => (
              <FormControlLabel
                control={<Radio />}
                key={subMissionActorCase}
                label={QUOTATION_CASE_LABELS[subMissionActorCase]}
                value={subMissionActorCase}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid size={12}>
        <Typography component="h3" variant="subtitle1">
          Acteurs
        </Typography>
      </Grid>
      {refusals.length ? (
        <Grid size={12}>
          <ErrorsAlert errors={refusals} />
        </Grid>
      ) : null}
      {isInternalQuotation ? (
        <Grid size={12}>
          <NoticesAlert notices={notices} />
        </Grid>
      ) : null}
      <Grid size={{ xs: 12, md: 6 }}>
        <QuotationActorSelect
          disabled={!isInternalQuotation}
          error={
            isInternalQuotation && typeof errors.internalActors === 'string'
              ? errors.internalActors
              : undefined
          }
          id="sub-mission-quotation-emitter"
          label="Émetteur du devis"
          onChange={handleEmitterChange}
          options={
            isInternalQuotation ? quotationEmitterCandidates : actorOptions
          }
          value={
            isInternalQuotation
              ? chosenEmitterThirdPartyId
              : emitterThirdPartyId
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <QuotationActorSelect
          disabled
          id="sub-mission-quotation-receiver"
          label="Destinataire du devis"
          options={actorOptions}
          value={
            isInternalQuotation ? emitterThirdPartyId : receiverThirdPartyId
          }
        />
      </Grid>
    </Grid>
  );
};
